import type {
  IPermissionTypeMapping,
  IUserPermissionsManager,
  PermissionCheckItem,
  PermissionInfo,
} from '../type';

import { useUserStore } from '@stores/user';

import {
  BASIC_PAGE_LIMIT,
  FREE_PAGE_LIMIT,
  PermissionCheckType,
  PermissionType,
  PRO_PAGE_LIMIT,
  UNLIMITED,
  Unlimited,
} from '../const';

/** 权益类型映射配置 */
const PERMISSION_TYPE_MAPPING: IPermissionTypeMapping = {
  [PermissionType.UNLIMITED_CREDIT]: {
    backendType: PermissionCheckType.UNLIMITED_CREDIT,
    useBackendQuantity: true,
  },
  [PermissionType.PUBLISH_WEBSITE_NUM]: {
    backendType: PermissionCheckType.PUBLISH_WEBSITE_NUM,
    useBackendQuantity: true,
  },
  [PermissionType.PAGE_COUNT_LIMIT]: {
    backendType: PermissionCheckType.PAGE_NUM_PER_WEBSITE,
    useBackendQuantity: true,
  },
  [PermissionType.CUSTOM_CODE]: {
    backendType: PermissionCheckType.CUSTOM_CODE,
  },
  [PermissionType.CUSTOM_HOST]: {
    backendType: PermissionCheckType.CUSTOM_DOMAIN,
  },
  [PermissionType.REMOVE_BADGE]: {
    backendType: PermissionCheckType.REMOVE_BRAND,
  },
  [PermissionType.FREE_CUSTOM_DOMAIN]: {
    backendType: PermissionCheckType.FREE_CUSTOM_DOMAIN,
  },
  [PermissionType.GA]: {
    backendType: PermissionCheckType.GOOGLE_ANALYZE,
  },
};

/** 需要处理数量的权益类型集合 */
const QUANTITY_PERMISSION_TYPES = new Set([
  PermissionCheckType.PUBLISH_WEBSITE_NUM,
  PermissionCheckType.PAGE_NUM_PER_WEBSITE,
  PermissionCheckType.CUSTOM_CODE,
  PermissionCheckType.PUBLISH_REMAIN_TIME,
]);

/** 不同套餐的默认权益 */
const BASE_RIGHTS_CONFIG: Record<string, Partial<Record<PermissionType, number>>> = {
  FREE: {
    [PermissionType.PAGE_COUNT_LIMIT]: FREE_PAGE_LIMIT,
  },
  BASIC: {
    [PermissionType.PAGE_COUNT_LIMIT]: BASIC_PAGE_LIMIT,
    [PermissionType.REMOVE_BADGE]: Unlimited,
  },
  PRO: {
    [PermissionType.PAGE_COUNT_LIMIT]: PRO_PAGE_LIMIT,
    [PermissionType.CUSTOM_HOST]: Unlimited,
    [PermissionType.GA]: Unlimited,
    [PermissionType.CUSTOM_CODE]: Unlimited,
    [PermissionType.REMOVE_BADGE]: Unlimited,
  },
  PREMIUM: {
    [PermissionType.PAGE_COUNT_LIMIT]: PRO_PAGE_LIMIT,
    [PermissionType.CUSTOM_HOST]: Unlimited,
    [PermissionType.GA]: Unlimited,
    [PermissionType.CUSTOM_CODE]: Unlimited,
    [PermissionType.REMOVE_BADGE]: Unlimited,
    [PermissionType.UNLIMITED_CREDIT]: Unlimited,
  },
};

/**
 * 处理字符串转数字和无限制标识
 */
function parseQuantityValue(value: string | number | undefined): number {
  if (value === undefined) {
    return 0;
  }

  const parsedNum = typeof value === 'string' ? Number.parseInt(value, 10) : value;
  return parsedNum === UNLIMITED ? Unlimited : (parsedNum || 0);
}

/**
 * 转换权益列表为对象
 */
function transformPermissionList(
  permissions: Partial<Record<PermissionType, number>>,
): Partial<Record<PermissionType, PermissionInfo>> {
  const result: Partial<Record<PermissionType, PermissionInfo>> = {};

  Object.entries(permissions).forEach(([key, quantity]) => {
    result[key as PermissionType] = { quantity: quantity as number };
  });

  return result;
}

/**
 * 处理后端权益项
 */
function processBackendPermissionItem(item: PermissionCheckItem): PermissionCheckItem {
  const basePermission = {
    hasPermission: item.hasPermission,
    type: item.type,
  };

  if (QUANTITY_PERMISSION_TYPES.has(item.type)) {
    return {
      ...basePermission,
      content: {
        num: parseQuantityValue(item.content?.num),
      },
    };
  }

  return basePermission;
}

export function userPermissionsManager(): IUserPermissionsManager {
  const { userPermissionList } = useUserStore.getState();

  /**
   * 获取前端兜底的权益列表
   */
  const getFrontendDefaultPermissionList = (
    bundle: string,
    options?: { points?: number },
  ): Partial<Record<PermissionType, PermissionInfo>> => {
    const points = options?.points || 0;
    const baseRights = BASE_RIGHTS_CONFIG[bundle] || BASE_RIGHTS_CONFIG.FREE;

    const permissionsWithPoints = {
      [PermissionType.POINTS]: points,
      ...baseRights,
    };

    return transformPermissionList(permissionsWithPoints);
  };

  /**
   * 后端返回的权益列表
   */
  const getBackendPermissionsList = (): Record<PermissionCheckType, PermissionCheckItem> => {
    const permissions = {} as Record<PermissionCheckType, PermissionCheckItem>;

    if (!userPermissionList) {
      return permissions;
    }

    userPermissionList.forEach((item) => {
      permissions[item.type] = processBackendPermissionItem(item);
    });

    return permissions;
  };

  /**
   * 最终的权益
   */
  const getFinalPermissionList = (
    permission: PermissionType,
  ): { hasPermission: boolean; quantity: number } => {
    const bundleRights = getFrontendDefaultPermissionList('FREE');
    const backendPermissions = getBackendPermissionsList();
    const mapping = PERMISSION_TYPE_MAPPING[permission];

    if (mapping) {
      const backendPermission = backendPermissions[mapping.backendType];
      const bundleRight = bundleRights[permission];

      return {
        hasPermission: backendPermission?.hasPermission ?? !!bundleRight,
        quantity: mapping.useBackendQuantity
          ? ((backendPermission?.content?.num as number) ?? bundleRight?.quantity ?? 0)
          : bundleRight?.quantity ?? 0,
      };
    }

    const bundleRight = bundleRights[permission];
    return {
      hasPermission: !!bundleRight,
      quantity: bundleRight?.quantity ?? 0,
    };
  };

  return {
    getFrontendDefaultPermissionList,
    getBackendPermissionsList,
    getFinalPermissionList,
  };
}
